import React from 'react';
import Decimal from 'decimal.js';
import {
  Button,
  InputNumber,
  Progress,
  Switch,
  Tag,
  Tooltip,
} from '@bedrock/components';
import { AlertTriangle, History, Refresh } from '@bedrock/icons-react';
import type { DescriptionsItemType } from '@bedrock/components/lib/Descriptions/Item';
import { CellDescriptions } from '@mpc/components';
import CopyableNode from '../../components/CopyableNode';
import { financial, currencyEnum } from '../../utils/number';
import type {
  BudgetAdjustItemVo,
  BudgetDemandItemVo,
  KpiIndicatorVo,
} from './types';
import { isSummaryRow } from './types';
import { getDemandStateTagColor } from './demandStateColor';

import type { AlertLevelEnum, Money } from '../../services/api';

// ─── 通用：金额格式化 ──────────────────────────────────────────────────────
export function formatMoney(
  money?: { amount?: number; currency?: string } | null
): string {
  if (!money || money.amount === undefined || money.amount === null)
    return '--';
  const label =
    currencyEnum[
      (money.currency ?? 'rmb').toLowerCase() as keyof typeof currencyEnum
    ] ?? '';
  const formatted = financial(money.amount);
  return `${formatted} ${label}`;
}

/**
 * 金额是否有有效值（与 formatMoney 的"--"判定保持一致）。
 * 用于合计行决定是否需要加粗 —— 仅在金额有值时加粗，空值仍保持默认字重。
 */
export function hasMoneyValue(
  money?: { amount?: number; currency?: string } | null
): boolean {
  return !!money && money.amount !== undefined && money.amount !== null;
}

// ─── 单号 + 复制 + 状态 Tag ─────────────────────────────────────────────────
export interface OrderNumberCellProps {
  number?: string;
  /**
   * 状态显示文本（中文）。后端 `plan_demand_state` / `demand_state` 字段
   * 已是中文，直接传入即可作为 Tag 文本。
   *
   * 兼容旧调用：若仅传入 `state` 而未传 `stateEnum`，且 `state` 等于
   * 枚举大写值（如历史数据传 'DRAFT' 字符串），仍可触发草稿态特殊渲染。
   */
  state?: string;
  /**
   * 状态枚举值（DemandStateEnum），用于决定 Tag 颜色。
   * 对应后端 `plan_demand_state_enum` / `demand_state_enum` 字段。
   *
   * 颜色规则参见 `./demandStateColor.ts`：
   *   - 需要处理节点 → light-orange（黄）
   *   - 流程进行中   → light-blue  （蓝）
   *   - 结束节点     → default     （灰）
   */
  stateEnum?: string;
  onClick?: () => void;
}

export const OrderNumberCell: React.FC<OrderNumberCellProps> = ({
  number,
  state,
  stateEnum,
  onClick,
}) => {
  // 草稿态判定优先级：stateEnum > state（兼容历史调用）
  // 草稿态二级单尚未拿到正式单号，需在无单号场景下也能渲染「草稿」Tag。
  const upperEnum =
    typeof stateEnum === 'string' ? stateEnum.toUpperCase() : '';
  const upperState = typeof state === 'string' ? state.toUpperCase() : '';
  const isDraft = upperEnum === 'DRAFT' || upperState === 'DRAFT';

  if (!number) {
    if (isDraft) {
      // 草稿态使用 light-orange，与正式单号上的"流程中"Tag（light-blue）做视觉区分
      return (
        <div>
          <div>--</div>
          <Tag size="xSmall" color="light-orange" text="草稿" />
        </div>
      );
    }
    return <span>--</span>;
  }

  // Tag 文本：草稿态固定显示「草稿」；其他状态优先用后端中文 state 文本，
  // 没有 state 时回退到 stateEnum（避免完全无文本）。
  const tagText = isDraft ? '草稿' : state || stateEnum;
  // Tag 颜色：草稿态固定 light-orange；其他状态按 stateEnum 经映射表派生，
  // 未传 stateEnum 时退化到 light-blue（保持旧行为）。
  const tagColor = isDraft ? 'light-orange' : getDemandStateTagColor(stateEnum);

  return (
    <div>
      <CopyableNode value={number}>
        {onClick ? (
          <Button type="link" size="small" onClick={onClick}>
            {number}
          </Button>
        ) : (
          // 不传 onClick 时退化为纯文本（不渲染 link 样式的按钮）。
          // 典型场景：草稿态二级单尚未跳转到详情页，单号本身不可点击，
          // 仅展示号码 + 复制按钮 + 「草稿」Tag。
          <span className="text-color-1">{number}</span>
        )}
      </CopyableNode>
      {tagText && (
        <Tag
          size="xSmall"
          color={tagColor}
          text={tagText}
          className="whitespace-nowrap"
        />
      )}
    </div>
  );
};

// ─── 执行周期（两行展示）──────────────────────────────────────────────────
export const CycleCell: React.FC<{ begin?: string; end?: string }> = ({
  begin,
  end,
}) => {
  if (!begin || !end) return <span>--</span>;
  return (
    <div className="flex flex-col text-color-1">
      <span>{begin}</span>
      <span>至 {end}</span>
    </div>
  );
};

// ─── 预算金额 + 变更记录链接 ─────────────────────────────────────────────────
export interface BudgetCellProps {
  money?: BudgetDemandItemVo['budget'];
  showChangeRecord?: boolean;
  onViewChangeRecord?: () => void;
  /**
   * 是否为合计行（CYCLE_SUMMARY / GRADIENT_SUMMARY）。
   * 合计行且金额有值时，金额文本加粗以与明细行做视觉区分；
   * 金额为空（显示 '--'）时不加粗。
   */
  isSummary?: boolean;
}

export const BudgetCell: React.FC<BudgetCellProps> = ({
  money,
  showChangeRecord,
  onViewChangeRecord,
  isSummary,
}) => {
  const colorCls = showChangeRecord
        ? 'text-warning-3'
        : 'text-color-1';
  const boldCls = isSummary && hasMoneyValue(money) ? 'font-bold' : '';
  return (
    <div className="flex flex-col gap-1 items-end">
      <span className={[colorCls, boldCls].filter(Boolean).join(' ')}>
        {formatMoney(money)}
      </span>
      {showChangeRecord && onViewChangeRecord && (
        // 使用 @bedrock/components Button：
        //   - type="text"：文字按钮，无背景无边框，贴合表格内联链接的视觉
        //   - size="small"：与单元格内字号 / 行高匹配
        //   - icon：直接用 @bedrock/icons-react 的 History 图标，组件内部
        //     会处理图标和文字的间距，无需手写 gap-1
        //   - stopPropagation：避免冒泡到行点击 / 排序等表格行为
        //   - 外层 w-fit 让按钮宽度收紧到内容，不撑满单元格
        <Button
          type="text"
          size="xSmall"
          icon={<History />}
          className="w-fit p-0"
          onClick={(e) => {
            e.stopPropagation();
            onViewChangeRecord();
          }}
        >
          变更记录
        </Button>
      )}
    </div>
  );
};

// ─── 进度（预算 + 时间 双进度条） ───────────────────────────────────────────
export interface ProgressCellProps {
  budgetProgress?: number;
  timeProgress?: number;
}

export const ProgressCell: React.FC<ProgressCellProps> = ({
  budgetProgress = 0,
  timeProgress = 0,
}) => {
  const hasRisk = budgetProgress > timeProgress;
  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center gap-2">
        <span className="text-xs text-color-3 w-8 shrink-0">预算:</span>
        <Progress
          percent={budgetProgress}
          strokeColor={hasRisk ? 'var(--status-warning-1)' : undefined}
          size="small"
          renderInfo={(text,)=><span>{budgetProgress}%</span>}
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-color-3 w-8 shrink-0">时间:</span>
        <Progress
          percent={timeProgress}
          size="small"
          strokeColor={hasRisk ? 'var(--status-warning-1)' : undefined}
          renderInfo={(text,)=><span>{timeProgress}%</span>}
        />
      </div>
    </div>
  );
};

// ─── 预估剩余可用天数（高风险 Tag） ──────────────────────────────────────────
export const RemainDaysCell: React.FC<{
  days?: number;
  alertEnabled?: boolean;
  alertLevel?: AlertLevelEnum;
}> = ({ days, alertEnabled, alertLevel }) => {
  if (days === undefined || days === null) return <span>--</span>;
  const isHigh = alertEnabled && alertLevel === 'HIGH';
  return (
    <div className="flex items-center gap-2">
      <span className={isHigh ? 'text-danger-3' : 'text-color-1'}>
        {days.toFixed(2)}
      </span>
      {isHigh && (
        <Tag size="xSmall" color="red" icon={<AlertTriangle />} text="风险" />
      )}
    </div>
  );
};

// ─── KPI 拆列渲染：使用 CellDescriptions 实现「最多 N 行 + 折叠」效果 ────────
/** 字段类型 */
export type KpiField = 'name' | 'orderActual' | 'effectActual';

/**
 * 构建 CellDescriptions 的 items
 * @param kpis KPI 数组
 * @param field 当前列要展示的字段
 *  - name: 仅显示 KPI 名称
 *  - orderActual: 显示「实际值/期望值」+ pass Tag
 *  - effectActual: 显示「效果值/期望值」+ effect_pass Tag
 */
/**
 * 解析 KpiIndicatorVo.kpi_schema（JSON 字符串）为对象。
 * kpi_schema 在 API 层定义为 string，实际内容是 DemandKpiSchemaData 序列化后的 JSON。
 */
function parseKpiSchema(kpiSchema: string | undefined): {
  type?: string;
  percent?: number;
} {
  if (!kpiSchema) return {};
  try {
    return JSON.parse(kpiSchema);
  } catch {
    return {};
  }
}

/**
 * 将 KPI 数值根据 kpi_schema 格式化展示：
 * - kpi_schema.type === 'numeric' && kpi_schema.percent === 1 → 乘以100并保留两位小数，后缀 '%'
 * - kpi_schema.type === 'numeric' → 保留两位小数
 * - 其他（plain_text 等）→ 原样返回字符串
 * - undefined / null / '--' / 非数字 → '--'
 */
function formatKpiValue(value: unknown, kpiSchema?: string): string {
  if (value === undefined || value === null || value === '--') return '--';
  const schema = parseKpiSchema(kpiSchema);
  if (schema.type === 'numeric') {
    const num = Number(value);
    if (!Number.isFinite(num)) return '--';
    if (schema.percent === 1) {
      return new Decimal(num).mul(100).toDecimalPlaces(2).toFixed(2) + '%';
    }
    return num.toFixed(2);
  }
  // plain_text 或未定义类型：原样返回
  const str = String(value);
  return str === '' ? '--' : str;
}

function buildKpiDescriptionItems(
  kpis: KpiIndicatorVo[],
  field: KpiField
): DescriptionsItemType[] {
  return kpis.map((kpi, idx) => {
    const key = kpi.kpi_code ?? `${idx}`;

    if (field === 'name') {
      return {
        key,
        label: null,
        children: <span className="text-color-1">{kpi.kpi_name ?? '--'}</span>,
      } as DescriptionsItemType;
    }

    const rawValue =
      field === 'orderActual' ? kpi.actual_value : kpi.effect_value;
    const rawExpect = kpi.expect_value;
    const pass = field === 'orderActual' ? kpi.pass : kpi.effect_pass;

    // pass === true → 达预期（绿色）；pass === false → 未达预期（红色）；undefined → 默认色
    const valueCls =
      pass === true
        ? 'text-success-3'
        : pass === false
          ? 'text-danger-3'
          : 'text-color-1';

    const formattedValue = formatKpiValue(rawValue, kpi.kpi_schema);
    const formattedExpect = formatKpiValue(rawExpect, kpi.kpi_schema);

    return {
      key,
      label: null,
      children: (
        <Tooltip
          title={
            <div>
              {kpi.kpi_name && <div>{kpi.kpi_name}</div>}
              <div>期望值：{rawExpect ?? '--'}</div>
              <div>实际值：{kpi.actual_value ?? '--'}</div>
              {kpi.effect_value !== undefined && (
                <div>效果值：{kpi.effect_value}</div>
              )}
            </div>
          }
        >
          <div className="flex items-center gap-1 cursor-default whitespace-nowrap">
            <span className={valueCls}>
              {formattedValue}
              {rawExpect !== undefined && rawExpect !== null && (
                <span className="text-color-3">/{formattedExpect}</span>
              )}
            </span>
            {pass === true && (
              <Tag size="xSmall" color="light-green" text="达预期" />
            )}
            {pass === false && (
              <Tag size="xSmall" color="red" text="未达预期" />
            )}
          </div>
        </Tooltip>
      ),
    } as DescriptionsItemType;
  });
}

export interface KpiCellProps {
  kpis?: KpiIndicatorVo[];
  field: KpiField;
  /** 最多显示行数，超出折叠到 +N popover 中 */
  maxDisplay?: number;
}

export const KpiCell: React.FC<KpiCellProps> = ({
  kpis,
  field,
  maxDisplay = 2,
}) => {
  if (!kpis || kpis.length === 0) return <span>--</span>;
  const items = buildKpiDescriptionItems(kpis, field);
  return (
    <CellDescriptions
      items={items}
      maxDisplayCount={maxDisplay}
      column={1}
      colon={false}
      otherLabel=""
    />
  );
};

// ─── 风险等级 Tag ────────────────────────────────────────────────────────
const ALERT_LEVEL_COLOR: Record<AlertLevelEnum, 'red' | 'orange' | 'green'> = {
  HIGH: 'red',
  MEDIUM: 'orange',
  LOW: 'green',
};
const ALERT_LEVEL_TEXT: Record<AlertLevelEnum, string> = {
  HIGH: '高风险',
  MEDIUM: '中风险',
  LOW: '正常',
};

export const RiskLevelCell: React.FC<{ level?: AlertLevelEnum }> = ({
  level,
}) => {
  if (!level || !ALERT_LEVEL_TEXT[level]) return <span>--</span>;
  return (
    <Tag
      size="xSmall"
      color={ALERT_LEVEL_COLOR[level]}
      text={ALERT_LEVEL_TEXT[level]}
    />
  );
};

// ─── 风险预警行级开关 ───────────────────────────────────────────────────────
export interface AlertSwitchCellProps {
  record: BudgetDemandItemVo;
  enabled?: boolean;
  onToggle?: (record: BudgetDemandItemVo, enabled: boolean) => void;
}

export const AlertSwitchCell: React.FC<AlertSwitchCellProps> = ({
  record,
  enabled,
  onToggle,
}) => {
  // 合计行不显示开关
  if (isSummaryRow(record)) return null;
  return (
    <div className="flex items-center gap-1">
      <Switch
        checked={!!enabled}
        onChange={(checked) => onToggle?.(record, checked)}
        labelOn="开启"
        labelOff="关闭"
      />
    </div>
  );
};

// ─── 合计行标签辅助 ────────────────────────────────────────────────────────
/**
 * 合计行在"二级单号"列展示的文案。
 *
 * 设计稿规则（与 CYCLE_SUMMARY / GRADIENT_SUMMARY 两种合计行对应）：
 *  - CYCLE_SUMMARY（周期合计行）：二级单号列**留空**（梯度列已显示"合计"）
 *  - GRADIENT_SUMMARY（梯度合计行）：二级单号列显示"合计"
 *    （不拼接梯度名，梯度名由梯度列独立展示）
 *
 * @returns 展示文案；返回空串表示该单元格不展示文字（列渲染层应降级为 `--`）
 */
export function getSummaryLabel(record: BudgetDemandItemVo): string {
  if (record.data_type === 'GRADIENT_SUMMARY') return '合计';
  // CYCLE_SUMMARY 及其他情况：二级单号列不展示文字
  return '';
}

// ─── budgetAdjust 模式专用渲染器 ────────────────────────────────────────────

/**
 * 调整金额展示（只读 render，编辑由 ProTablePlusForm 的 editRender 接管）。
 * 正数前缀「+」红色；负数前缀「-」绿色；0 黑色；空显示占位。
 */
export const AdjustAmountCell: React.FC<{ value?: number }> = ({ value }) => {
  if (value === undefined || value === null)
    return <span className="text-color-3">--</span>;
  if (value === 0) return <span className="text-color-1">0.00</span>;
  const isPositive = value > 0;
  const colorCls = isPositive ? 'text-danger-3' : 'text-success-3';
  const sign = isPositive ? '+ ' : '- ';
  return (
    <span className={colorCls}>
      {sign}
      {financial(Math.abs(value))}
    </span>
  );
};

/**
 * 调整后金额（预算金额 / 预估剩余可用天数 共用样式）。
 * 当 changed=true 时显示橙色高亮，表示该行已被调整过。
 */
export const AdjustedValueCell: React.FC<{
  text: string;
  changed?: boolean;
}> = ({ text, changed }) => (
  <span className={changed ? 'text-warning-3' : 'text-color-1'}>{text}</span>
);

/**
 * 「调整后预估剩余费用可用天数」单元格。
 *
 * 展示规则（与产品对齐）：
 *   - days 为 undefined（不可估算：日均花费缺失/为 0）→ 显示 '--'，不打高风险
 *   - highRisk=true → 红字 + 「高风险」Tag（覆盖 changed 黄色样式）
 *   - 否则按 changed 决定文本色（changed=true 黄色，false 黑色）
 *
 * 数值统一保留 2 位小数，与公式精度 0.01 一致；负数（已超预算）原样展示。
 */
export const AdjustedRemainDaysCell: React.FC<{
  days?: number;
  changed?: boolean;
  highRisk?: boolean;
  bold?: boolean;
}> = ({ days, changed, highRisk, bold }) => {
  if (days === undefined || days === null) {
    return <span className="text-color-3">--</span>;
  }
  const text = days.toFixed(2);
  // 优先级：highRisk > changed > 默认
  const colorCls = highRisk
    ? 'text-danger-3'
    : changed
      ? 'text-warning-3'
      : 'text-color-1';
  return (
    <div className="flex items-center gap-2">
      <span className={`${bold ? 'font-bold ' : ''}${colorCls}`}>{text}</span>
      {highRisk && (
        <Tag size="xSmall" color="red" icon={<AlertTriangle />} text="风险" />
      )}
    </div>
  );
};

/**
 * 「日均花费（调整后）」复合编辑单元格：
 *   ┌──────────────────────────────┐
 *   │           ↻ 恢复初始值        │
 *   │  ┌────────────┐ ┌──────┐    │
 *   │  │ 50,000.00  │ │人民币│    │
 *   │  └────────────┘ └──────┘    │
 *   └──────────────────────────────┘
 *
 * - 已被修改（与 initial 不一致）时显示「恢复初始值」按钮
 * - 输入框为受控 InputNumber，change 透出完整 Money 结构
 * - 单位 chip 取行 currency
 */
export interface AdjustedDailyAvgFeeCellProps {
  record: BudgetAdjustItemVo;
  index: number;
  onChange?: (
    record: BudgetAdjustItemVo,
    index: number,
    next: Money | undefined
  ) => void;
  onReset?: (record: BudgetAdjustItemVo, index: number) => void;
}

export const AdjustedDailyAvgFeeCell: React.FC<
  AdjustedDailyAvgFeeCellProps
> = ({ record, index, onChange, onReset }) => {
  if (isSummaryRow(record)) return <span className="text-color-3"></span>;

  const initial = record.daily_avg_fee;
  const current = record.adjusted_daily_avg_fee ?? record.daily_avg_fee;
  const currency = current?.currency ?? initial?.currency ?? 'rmb';
  const currencyLabel =
    currencyEnum[
      (currency as string).toLowerCase() as keyof typeof currencyEnum
    ] ?? '';

  const isChanged =
    initial !== undefined &&
    current !== undefined &&
    (initial.amount ?? null) !== (current.amount ?? null);

  const handleChange = (val: number | undefined) => {
    onChange?.(record, index, {
      amount: val,
      currency: current?.currency ?? initial?.currency,
    } as Money);
  };

  return (
    <div className="flex flex-col gap-1">
      {isChanged && (
        <Button
          type="link"
          size="small"
          icon={<Refresh />}
          onClick={() => onReset?.(record, index)}
          className="self-end !px-0"
        >
          恢复初始值
        </Button>
      )}
      <InputNumber
        size="small"
        value={current?.amount}
        precision={2}
        // 日均花费（调整后）业务约束：必须 >= 0，禁止负数
        min={0}
        placeholder="请输入"
        onChange={handleChange}
        addonAfter={currencyLabel}
        addonBlock
        direction="vertical"
        style={{ width: '100%' }}
        formatter={(value) => {
          const str = `${value ?? ''}`;
          if (str === '' || str === '-') return str;
          // precision=2 在配置了 formatter 后不生效，需手动四舍五入保留两位小数
          const num = parseFloat(str);
          if (isNaN(num)) return str;
          const rounded = num.toFixed(2);
          // 拆分整数部分与小数部分，整数部分加千位分隔符
          const [intPart, decPart] = rounded.split('.');
          const formattedInt = (intPart ?? '').replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ','
          );
          return `${formattedInt}.${decPart}`;
        }}
        // 过滤掉负号 / 货币符号 / 千分位逗号，确保即使粘贴负数也会被剥离为正值
        parser={(value) => (value || '').replace(/[$\s,-]/g, '')}
      />
    </div>
  );
};
